// src/hooks/useOfferPackage.ts

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { IOfferPackage } from "../../interfaces/IOfferpackage";
import { customFetcher } from "../useFetch";

//
// Fetch alle OfferPackages
//
export const useGetOfferPackages = () => {
    const { data, isLoading, error } = useQuery<IOfferPackage[], Error>({
        queryKey: ["offerPackages"],
        queryFn: () => customFetcher({ endpoint: "/offer-packages/" }),
        refetchOnWindowFocus: false,
        initialData: [],
    });
    return {
        offerPackages: data!,
        isLoadingOfferPackages: isLoading,
        errorOfferPackages: error,
    };
};

//
// Fetch ein einzelnes OfferPackage nach ID
//
export const useGetOfferPackage = (id: number | null) => {
    const { data, isLoading, error } = useQuery<IOfferPackage, Error>({
        queryKey: ["offerPackage", id],
        queryFn: () => customFetcher({ endpoint: `/offer-packages/${id}/` }),
        enabled: Boolean(id),
        refetchOnWindowFocus: false,
    });
    return {
        offerPackage: data,
        isLoadingOfferPackage: isLoading,
        errorOfferPackage: error,
    };
};

//
// Erstellen eines neuen OfferPackage
//
export const useCreateOfferPackage = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: createOfferPackage } = useMutation<IOfferPackage, Error, IOfferPackage>({
        mutationFn: (pkg) =>
            customFetcher({
                endpoint: "/offer-packages/",
                method: "POST",
                body: pkg,
            }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ["offerPackages"] });
        },
    });
    return { createOfferPackage };
};

//
// Update eines existierenden OfferPackage
//
export const useUpdateOfferPackage = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: updateOfferPackage } = useMutation<
        IOfferPackage,
        Error,
        { id: number; payload: IOfferPackage }
    >({
        mutationFn: ({ id, payload }) =>
            customFetcher({
                endpoint: `/offer-packages/${id}/`,
                method: "PUT",
                body: payload,
            }),
        onSuccess: (_, { id, payload }) => {
            void queryClient.invalidateQueries({ queryKey: ["offerPackages"] });
            void queryClient.invalidateQueries({ queryKey: ["offerPackage", id] });
        },
    });
    return { updateOfferPackage };
};

//
// Löschen eines OfferPackage
//
export const useDeleteOfferPackage = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: deleteOfferPackage } = useMutation<void, Error, number>({
        mutationFn: (id) =>
            customFetcher({
                endpoint: `/offer-packages/${id}/`,
                method: "DELETE",
            }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ["offerPackages"] });
        },
    });
    return { deleteOfferPackage };
};
